import React, { useContext, useState } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import message from "antd/lib/message";
import Radio from "antd/lib/radio";
import Form from "antd/lib/form";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerGrid,
  AnswerList,
  AnswerOption,
  SurveyFooter,
  SurveyProgressBar,
  FooterActions,
  FormItemStyle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface FeverFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const FeverForm: React.FC<FeverFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [currentPage, setCurrentPage] = useState(1);
  const [showSubmit, setShowSubmit] = useState(false);

  const [form] = Form.useForm();
  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);

  const userRole = sessionStorage.getItem("userRole");

  const { isLoading: isLoadingFeverMutation, mutate: submitFeverForm } =
    useMutation<string>(
      "add-bill",
      async () => {
        return await createApiClient().addFeverFormData({
          patient_internal_id:
            userRole === UserRoles.PATIENT && userContext.userData
              ? userContext.userData?.internal_id
              : patientContext?.patientData?.id || "",
          fever: localStorage.getItem("hasHadFever") || "",
          level: localStorage.getItem("howSevereIsTheFever") || "",
          frequency: localStorage.getItem("howOftenHaveYouHadFever") || "",
          submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
        });
      },
      {
        onSuccess: () => {
          message.success("Form Submitted Successfully");
          goToNextFormOrReturn();
        },
        onError: () => {
          message.error({
            content: (
              <span>
                There was a problem submitting this form, resubmit or{" "}
                <a onClick={() => goToNextFormOrReturn()}>
                  click here to continue to the next survey
                </a>
              </span>
            ),
            style: {
              bottom: "10px",
              position: "fixed",
              left: "30%"
            },
            duration: 5
          });
        }
      }
    );

  const onFinish = () => {
    //Values to be passed on to API from session storage
    if (localStorage.getItem("hasHadFever") === "No") {
      form.resetFields(["How severe", "How often"]);
      localStorage.setItem("howSevereIsTheFever", "");
      localStorage.setItem("howOftenHaveYouHadFever", "");
      submitFeverForm();
    } else if (currentPage === 1) {
      setCurrentPage(2);
    } else {
      submitFeverForm();
    }
  };

  const onClear = () => {
    if (currentPage === 1) {
      form.resetFields(["Have you had fever"]);
    } else {
      form.resetFields(["How severe", "How often"]);
    }
  };

  return (
    <SurveyPage>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="fatigue"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          style={{ width: "100%" }}
        >
          <SurveyHeader title={`Survey - ${selectedItems[0]}`} />

          {currentPage === 1 && (
            <>
              <SurveyNoticeCard />

              <SurveyQuestionCard
                title="Have you had any fever over the last several days?"
                required
              >
                <FormItemStyle
                  name="Have you had fever"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("hasHadFever")}
                    onChange={e => {
                      if (e.target.value === "No") {
                        setShowSubmit(true);
                      } else {
                        setShowSubmit(false);
                      }
                      localStorage.setItem("hasHadFever", e.target.value);
                    }}
                    style={{ width: "100%" }}
                  >
                    <AnswerGrid>
                      <AnswerOption>
                        <Radio value="Yes">Yes</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="No">No</Radio>
                      </AnswerOption>
                    </AnswerGrid>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          {currentPage === 2 && (
            <>
              <SurveyQuestionCard title="How severe is the fever?" required>
                <FormItemStyle
                  name="How severe"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("howSevereIsTheFever")}
                    onChange={e =>
                      localStorage.setItem(
                        "howSevereIsTheFever",
                        e.target.value
                      )
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Less than 101">Less than 101</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="101 - 103">101 - 103</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="More than 103">More than 103</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Fever with shaking chills, or rigors">
                          Fever with shaking chills, or rigors
                        </Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>

              <SurveyQuestionCard
                title="How often have you had fever in the last 7 days?"
                required
              >
                <FormItemStyle
                  name="How often"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("howOftenHaveYouHadFever")}
                    onChange={e =>
                      localStorage.setItem(
                        "howOftenHaveYouHadFever",
                        e.target.value
                      )
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Once">Once</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="2 - 4 times">2 - 4 times</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="5 or more times">5 or more times</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          <SurveyFooter>
            <SurveyProgressBar currentPage={currentPage} totalPages={2} />

            <FooterActions>
              <Form.Item>
                <FormClearButton onClear={onClear} />
              </Form.Item>

              {currentPage !== 1 && (
                <AppButton
                  type={ButtonType.Primary}
                  buttonContent={
                    <>
                      <ArrowLeftOutlined /> Back
                    </>
                  }
                  onClick={() => setCurrentPage(1)}
                  style={{ width: "105px" }}
                />
              )}

              <Form.Item>
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent={
                    currentPage !== 1 || showSubmit ? (
                      "Submit"
                    ) : (
                      <>
                        Next <ArrowRightOutlined />
                      </>
                    )
                  }
                  htmlType="submit"
                  loading={isLoadingFeverMutation}
                />
              </Form.Item>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
